import React, { useState } from 'react';
import { withDefaultPrintChart } from '../../../../../hoc';
import { isNumber, setSoilColorQc, roundNumber } from '../../../../../utils';
import stringify from 'fast-json-stable-stringify';
import useDeepCompareEffect from 'use-deep-compare-effect';
import ApexCharts from "apexcharts";

const CuChart = ({
    results = [],
    chartTitle = '',
    yLabel,
    xLabel, 
    chartOptions,
    setDataURI,
}) => {
    
    const [options, setOptions] = useState(chartOptions);
    const [series, setSeries] = useState([
        {
            name: '',
            data: [],
        },
    ]);

    useDeepCompareEffect(() => {
        setSeries(() => {
            return [
                {
                    name: 'Z',
                    data: [
                        ...results.sort((a,b) => a.z - b.z)
                        .filter(ele => (isNumber(ele.cu) && isNumber(ele.z)))
                        .reduce((acc, ele, idx, arr) => {
                            if (idx === (arr.length - 1)){
                                return [...acc, [ele.cu, ele.z], [0, ele.z]];
                            } else if(idx === 0) {
                                return [
                                    ...acc, 
                                    [ele.cu, 0], 
                                    [ele.cu, ele.z]
                                ];
                            } else {
                                return [...acc, [ele.cu, ele.z]];
                            }
                        }, []),
                    ],
                },
            ];
        });

        setOptions(() => {
            const maxZ = Math.max(...results.map((ele) => ele.z));
            const maxCu = Math.max(...results.map((ele) => ele.cu));
            const minCu = Math.min(...results.map((ele) => ele.cu));
            const maxZScale = maxZ%2 === 0 ? maxZ : maxZ + 1;

            return {
                ...chartOptions,
                title: {
                    ...chartOptions['title'],
                    text: chartTitle
                },
                yaxis: {
                    ...chartOptions['yaxis'],
                    tickAmount: Math.ceil(maxZ) > 30 
                        ? Math.ceil(maxZScale)/2
                        : Math.ceil(maxZScale),
                    max: Math.ceil(maxZScale),
                    min: 0,
                    forceNiceScale: true,
                    showForNullSeries: true,
                    stepSize: 2,
                    title: {
                        ...chartOptions['yaxis']['title'],
                        text: yLabel,
                        offsetX: -15,
                        style: {
                            fontSize: '12mm'
                        }
                    },
                },
                xaxis: {
                    ...chartOptions['xaxis'],
                    type: 'numeric',
                    labels: {
                        ...chartOptions['xaxis']['labels'],
                        offsetY: 35
                    },
                    title: {
                        ...chartOptions['xaxis']['title'],
                        offsetY: 0,
                        text: xLabel,

                        style: {
                            fontSize: '12mm'
                        }
                    },
                    position: 'top',
                    max: Math.floor((maxCu + 10 - 1) / 10) * 10 + 10,
                    min: Math.floor(minCu),
                    axisBorder: {
                        show: false,
                    },
                    axisTicks: {
                        show: true,
                        color: '#888',
                        height: 5,
                    },
                    crosshairs: {
                        show: false,
                    }
                },
                dataLabels: {
                    ...chartOptions['dataLabels'],
                    offsetX: 20,
                    formatter: function (x, opt) {
                        const label = opt.w.globals.initialSeries[0].data[opt.dataPointIndex][0];
    
                        return label === 0
                            ? 
                                ''
                            :
                                opt.dataPointIndex % 2 === 0
                                ?
                                roundNumber(opt.w.globals.initialSeries[0].data[opt.dataPointIndex][0],2)
                                :
                                ''
                    },
                },
                fill: {
                    ...chartOptions['fill'],
                    colors: ['#333'],
                    opacity: 0.35,
                },
                stroke: {
                    ...chartOptions['stroke'],
                    colors: ['#111'],
                    width: 1,
                },
                annotations: {
                    ...chartOptions['annotations'],
                    position: 'back',
                    yaxis: [
                        ...results.sort((a,b) => a.z-b.z).reduce((acc, ele, idx, arr) => {
                            return [
                                ...acc,
                                {
                                    y: idx === 0 ? 0 : arr[idx - 1].z,
                                    y2: idx === (arr.length - 1) ? Math.ceil(ele.z) : ele.z,
                                    borderColor: 'transparent',
                                    opacity: 0.55,
                                    fillColor: setSoilColorQc({
                                        type: ele.soil,
                                        qc: ele.qc,
                                    }),
                                },
                            ];
                        }, []),
                    ],
                },

            };
        });
    }, [chartOptions, results, chartTitle, xLabel, yLabel]);

    useDeepCompareEffect(() => {
        let chart = new ApexCharts(document.querySelector("#cuChart"), {
            series: series,
            ...options,
            chart: {
                ...options['chart'],
                type: 'area'
            }
        });
        chart.render();

        chart.dataURI().then(({ imgURI }) => {
            setDataURI(imgURI)
        });

        return () => chart.destroy();

    },[series, options]);

    return (
        <div id="cuChart"></div>
    );
};

export default React.memo(
    withDefaultPrintChart(CuChart),
    (prev, next) => stringify(prev) === stringify(next),
);
